// Admin console: Akun (phase 4, account activation). Accounts are made by
// signing in with Google; the admin activates them after payment (outside
// the system), deactivates them, changes the role, or deletes them.
//   header         plain spacing, hairline below   h1, count
//   toolbar        divided section                 status tabs (Semua, Menunggu aktivasi,
//                                                  Aktif, Nonaktif: GET /users?account=) and search (q)
//   table          rows                            Carbon DataTable from 1024, stacked rows below
//   status_confirm dialog                          Aktifkan / Nonaktifkan (PATCH /users/:id/status)
//   history        dialog                          GET /users/:id/status-history
//   delete_confirm dialog                          names the account; on 409 offers Nonaktifkan
// Role change: a select per row (every role except pending and admin;
// konsultan allowed), PATCH /users/:id { role }. Admin rows carry no actions.
import { useId, useState } from "react";
import * as API from "../api";
import { formatCount, formatDateTime } from "../lib/format";
import { ROLE_LABEL, ROLE_OPTIONS } from "../lib/labels";
import { Badge, Dialog, EmptyState, Notice, PageHeader, SearchInput, SkeletonRows, Tabs, useAsync, useTitle } from "../ui/components";
import { Glyph } from "../ui/icons";

type Tab = "" | API.AccountStatus;

export const ACCOUNT_TABS: { id: string; label: string }[] = [
  { id: "all", label: "Semua" },
  { id: "menunggu_aktivasi", label: "Menunggu aktivasi" },
  { id: "aktif", label: "Aktif" },
  { id: "nonaktif", label: "Nonaktif" },
];

export const ACCOUNT_STATUS_LABEL: Record<API.AccountStatus, string> = {
  menunggu_aktivasi: "Menunggu aktivasi",
  aktif: "Aktif",
  nonaktif: "Nonaktif",
};

export function signInLabel(provider: API.User["authProvider"]): string {
  if (provider === "google") return "Google";
  if (provider === "google_demo") return "Google (simulasi)";
  return "Kata sandi";
}

/** Roles the admin may set from the row: never pending, never admin. */
export const ASSIGNABLE: API.Role[] = ROLE_OPTIONS.filter((r) => r !== "admin" && r !== "pending");

function StatusBadge({ status }: { status: API.AccountStatus }) {
  const tone = status === "aktif" ? "success" : status === "menunggu_aktivasi" ? "warning" : "neutral";
  return <Badge tone={tone}>{ACCOUNT_STATUS_LABEL[status]}</Badge>;
}

function activationText(u: API.AccountRow): string {
  if (!u.activatedAt) return "Belum diaktifkan";
  return `${formatDateTime(u.activatedAt)}${u.activatedBy ? `, oleh ${u.activatedBy.name}` : ""}`;
}

type Confirm = { user: API.AccountRow; to: "aktif" | "nonaktif" } | null;
type Notice_ = { tone: "success" | "danger"; title: string; body: string } | null;

export function AccountsScreen() {
  useTitle("Akun");
  const ids = useId();
  const [tab, setTab] = useState<Tab>("");
  const [q, setQ] = useState("");
  const [confirm, setConfirm] = useState<Confirm>(null);
  const [history, setHistory] = useState<API.AccountRow | null>(null);
  const [deleting, setDeleting] = useState<API.AccountRow | null>(null);
  const [notice, setNotice] = useState<Notice_>(null);
  const [roleBusy, setRoleBusy] = useState<string | null>(null);

  const users = useAsync(() => API.listUsers({ q: q.trim() || undefined, account: tab || undefined }), [tab, q.trim()]);
  const all = users.data ?? [];
  const filtered = Boolean(q.trim() || tab);

  function replace(next: API.User) {
    // A row that left the current tab drops out of the list.
    const keep = !tab || next.status === tab;
    users.set(keep ? all.map((u) => (u.id === next.id ? { ...u, ...next } : u)) : all.filter((u) => u.id !== next.id));
  }

  async function changeRole(u: API.AccountRow, role: API.Role) {
    if (role === u.role || role === "pending" || role === "admin") return;
    setRoleBusy(u.id);
    try {
      const next = await API.updateUser(u.id, { role });
      replace(next);
      setNotice({ tone: "success", title: `Peran ${u.name} diganti`, body: `Peran sekarang ${ROLE_LABEL[next.role]}.` });
    } catch (err) {
      setNotice({ tone: "danger", title: "Peran belum berubah", body: API.errorMessage(err) });
    } finally {
      setRoleBusy(null);
    }
  }

  const lead = users.data ? `${formatCount(all.length)} akun${filtered ? " cocok" : " terdaftar"}` : "Memuat akun";

  const actions = (u: API.AccountRow, compact = false) => {
    if (u.role === "admin") return <span className="cell-muted">Administrator</span>;
    return (
      <div className="row-actions">
        {/* The table row keeps the status switch icon-only (named in its tooltip) so Aktivasi keeps its width; the phone list shows the label. */}
        {u.status === "aktif" ? (
          <button
            type="button"
            className={compact ? "btn btn-ghost" : "ui-icon-btn"}
            onClick={() => setConfirm({ user: u, to: "nonaktif" })}
            aria-label={`Nonaktifkan ${u.name}`}
            title={compact ? undefined : "Nonaktifkan"}
          >
            <Glyph name="lock" />
            {compact ? <span>Nonaktifkan</span> : null}
          </button>
        ) : (
          <button
            type="button"
            className={compact ? "btn btn-ghost" : "ui-icon-btn"}
            onClick={() => setConfirm({ user: u, to: "aktif" })}
            aria-label={`Aktifkan ${u.name}`}
            title={compact ? undefined : "Aktifkan"}
          >
            <Glyph name="tickCircle" />
            {compact ? <span>Aktifkan</span> : null}
          </button>
        )}
        {compact ? null : (
          <button type="button" className="ui-icon-btn" aria-label={`Riwayat status ${u.name}`} title="Riwayat status" onClick={() => setHistory(u)}>
            <Glyph name="clock" />
          </button>
        )}
        <button type="button" className="ui-icon-btn" aria-label={`Hapus ${u.name}`} title="Hapus" onClick={() => setDeleting(u)}>
          <Glyph name="trash" />
        </button>
      </div>
    );
  };

  const roleCell = (u: API.AccountRow) =>
    u.role === "admin" ? (
      <span className="cell-text">{ROLE_LABEL.admin}</span>
    ) : (
      <select
        aria-label={`Peran ${u.name}`}
        value={u.role}
        disabled={roleBusy === u.id}
        onChange={(e) => void changeRole(u, e.currentTarget.value as API.Role)}
      >
        {u.role === "pending" ? (
          <option value="pending" disabled>
            {ROLE_LABEL.pending}
          </option>
        ) : null}
        {ASSIGNABLE.map((r) => (
          <option key={r} value={r}>
            {ROLE_LABEL[r]}
          </option>
        ))}
      </select>
    );

  return (
    <>
      <PageHeader divided title="Akun" lead={lead} />

      <section className="toolbar" aria-label="Saring akun">
        <Tabs label="Status akun" idBase={`${ids}tabs`} items={ACCOUNT_TABS} value={tab || "all"} onChange={(id) => setTab(id === "all" ? "" : (id as Tab))} />
        <div className="field-row" data-cols="3" role="search">
          <div className="field">
            <label htmlFor={`${ids}q`}>Cari</label>
            <SearchInput id={`${ids}q`} value={q} onChange={setQ} placeholder="Nama atau email" />
          </div>
        </div>
      </section>

      <section className="region" aria-label="Daftar akun" id={`${ids}tabs-panel`}>
        {notice ? (
          <Notice tone={notice.tone} title={notice.title}>
            <p>{notice.body}</p>
          </Notice>
        ) : null}

        {users.loading && !users.data ? (
          <SkeletonRows count={6} label="Memuat akun" />
        ) : users.error ? (
          <Notice tone="danger" title="Daftar akun belum dapat dimuat" action={<button type="button" className="btn btn-secondary" onClick={users.reload}>Muat ulang</button>}>
            <p>{API.errorMessage(users.error)}</p>
          </Notice>
        ) : all.length === 0 ? (
          <EmptyState
            icon="profile2user"
            title={filtered ? "Tidak ada akun yang cocok" : "Belum ada akun"}
            body={filtered ? "Ubah pencarian atau status yang dipilih." : "Akun baru muncul di sini setelah pengguna masuk dengan Google."}
          />
        ) : (
          <>
            <div className="records-table">
              <table className="table accounts-table">
                <thead>
                  <tr>
                    <th scope="col" className="col-name">Nama</th>
                    <th scope="col">Cara masuk</th>
                    <th scope="col" className="col-role">Peran</th>
                    <th scope="col">Status</th>
                    <th scope="col" className="col-xl">Aktivasi</th>
                    <th scope="col" className="action-col">
                      Tindakan
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {all.map((u) => (
                    <tr key={u.id} data-account={u.id}>
                      <td>
                        <span className="cell-title">{u.name}</span>
                        <span className="cell-meta" title={u.email}>
                          {u.email}
                        </span>
                      </td>
                      <td>{signInLabel(u.authProvider)}</td>
                      <td>{roleCell(u)}</td>
                      <td>
                        <StatusBadge status={u.status} />
                      </td>
                      <td className="col-xl" data-activation="">
                        {activationText(u)}
                      </td>
                      <td className="action-col">{actions(u)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <ul className="rows records-list">
              {all.map((u) => (
                <li key={u.id} className="row">
                  <span className="row-main">
                    <span className="row-title">{u.name}</span>
                    <span className="row-meta">
                      {ROLE_LABEL[u.role]}, {u.email}, {signInLabel(u.authProvider)}
                    </span>
                    <span className="row-meta">{activationText(u)}</span>
                  </span>
                  <span className="row-side">
                    <StatusBadge status={u.status} />
                  </span>
                  {actions(u, true)}
                </li>
              ))}
            </ul>
          </>
        )}
      </section>

      {confirm ? (
        <StatusDialog
          confirm={confirm}
          onClose={() => setConfirm(null)}
          onDone={(next) => {
            const u = confirm.user;
            setConfirm(null);
            replace(next);
            setNotice({
              tone: "success",
              title: next.status === "aktif" ? `Akun ${u.name} diaktifkan` : `Akun ${u.name} dinonaktifkan`,
              body: next.status === "aktif" ? "Semua fitur berbayar terbuka untuk akun ini." : "Pemilik akun tidak dapat memakai aplikasi sampai diaktifkan lagi.",
            });
          }}
        />
      ) : null}

      {history ? <HistoryDialog user={history} onClose={() => setHistory(null)} /> : null}

      {deleting ? (
        <DeleteAccountDialog
          user={deleting}
          onClose={() => setDeleting(null)}
          onDeleted={(u) => {
            setDeleting(null);
            users.set(all.filter((x) => x.id !== u.id));
            setNotice({ tone: "success", title: `Akun ${u.name} dihapus`, body: "Akun dan percakapan Pluto miliknya sudah dihapus." });
          }}
          onDeactivated={(u) => {
            setDeleting(null);
            replace(u);
            setNotice({ tone: "success", title: `Akun ${u.name} dinonaktifkan`, body: "Dokumen tetap tersimpan; pemilik akun tidak dapat memakai aplikasi lagi." });
          }}
        />
      ) : null}
    </>
  );
}

function StatusDialog({ confirm, onClose, onDone }: { confirm: Exclude<Confirm, null>; onClose: () => void; onDone: (u: API.User) => void }) {
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const { user, to } = confirm;
  const activate = to === "aktif";

  async function run() {
    setBusy(true);
    setFailure(null);
    try {
      onDone(await API.setUserStatus(user.id, to));
    } catch (err) {
      setFailure(API.errorMessage(err));
      setBusy(false);
    }
  }

  return (
    <Dialog
      open
      size="sm"
      busy={busy}
      title={activate ? `Aktifkan akun ${user.name}?` : `Nonaktifkan akun ${user.name}?`}
      description={user.email}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose} disabled={busy}>
            Batal
          </button>
          <button type="button" className="btn" data-tone={activate ? undefined : "danger"} onClick={run} aria-busy={busy || undefined}>
            {activate ? "Ya, aktifkan" : "Ya, nonaktifkan"}
          </button>
        </>
      }
    >
      <div className="confirm-body">
        {failure ? (
          <Notice tone="danger" title="Status akun belum berubah">
            <p>{failure}</p>
          </Notice>
        ) : null}
        <p className="confirm-text">
          {activate
            ? "Aktifkan setelah pembayaran diterima. Akun ini lalu dapat memakai project, lampiran, pembuatan dokumen, Dokumen, dan E-Learning."
            : "Akun ini tidak dapat memakai aplikasi sampai diaktifkan lagi. Data dan dokumennya tetap tersimpan."}
        </p>
      </div>
    </Dialog>
  );
}

function HistoryDialog({ user, onClose }: { user: API.AccountRow; onClose: () => void }) {
  const events = useAsync(() => API.getStatusHistory(user.id), [user.id]);
  const label = (s: string | null) => (s && s in ACCOUNT_STATUS_LABEL ? ACCOUNT_STATUS_LABEL[s as API.AccountStatus] : (s ?? "Baru"));
  return (
    <Dialog open size="sm" title={`Riwayat status ${user.name}`} description={user.email} onClose={onClose}>
      {events.loading && !events.data ? (
        <SkeletonRows count={3} label="Memuat riwayat" />
      ) : events.error ? (
        <Notice tone="danger" title="Riwayat belum dapat dimuat">
          <p>{API.errorMessage(events.error)}</p>
        </Notice>
      ) : (events.data ?? []).length === 0 ? (
        <p className="confirm-text">Belum ada perubahan status.</p>
      ) : (
        <ul className="rows">
          {(events.data ?? []).map((e) => (
            <li key={e.id} className="row">
              <span className="row-main">
                <span className="row-title">
                  {label(e.fromStatus)} ke {label(e.toStatus)}
                </span>
                <span className="row-meta">
                  {formatDateTime(e.changedAt)}
                  {e.changedBy ? `, oleh ${e.changedBy.name}` : ""}
                </span>
              </span>
            </li>
          ))}
        </ul>
      )}
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Delete, with the 409 path (the user still owns documents)
// ---------------------------------------------------------------------------
function DeleteAccountDialog({
  user,
  onClose,
  onDeleted,
  onDeactivated,
}: {
  user: API.AccountRow;
  onClose: () => void;
  onDeleted: (u: API.AccountRow) => void;
  onDeactivated: (u: API.User) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [owns, setOwns] = useState<string | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const canDeactivate = user.status !== "nonaktif";

  async function remove() {
    setBusy(true);
    setFailure(null);
    try {
      await API.deleteUser(user.id);
      onDeleted(user);
    } catch (err) {
      if (err instanceof API.ApiError && err.status === 409) setOwns(err.message);
      else setFailure(API.errorMessage(err));
      setBusy(false);
    }
  }

  async function deactivate() {
    setBusy(true);
    setFailure(null);
    try {
      onDeactivated(await API.setUserStatus(user.id, "nonaktif"));
    } catch (err) {
      setFailure(API.errorMessage(err));
      setBusy(false);
    }
  }

  const blocked = owns !== null;
  return (
    <Dialog
      open
      size="sm"
      busy={busy}
      title={blocked ? `Akun ${user.name} belum dapat dihapus` : `Hapus akun ${user.name}?`}
      description={`${user.email}, ${ROLE_LABEL[user.role]}`}
      onClose={onClose}
      footer={
        blocked ? (
          <>
            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={busy}>
              {canDeactivate ? "Batal" : "Tutup"}
            </button>
            {canDeactivate ? (
              <button type="button" className="btn" onClick={deactivate} aria-busy={busy || undefined}>
                <Glyph name="lock" />
                <span>Nonaktifkan akun</span>
              </button>
            ) : null}
          </>
        ) : (
          <>
            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={busy}>
              Batal
            </button>
            <button type="button" className="btn" data-tone="danger" onClick={remove} aria-busy={busy || undefined}>
              <Glyph name="trash" />
              <span>Hapus akun</span>
            </button>
          </>
        )
      }
    >
      <div className="confirm-body">
        {failure ? (
          <Notice tone="danger" title={blocked ? "Akun belum dinonaktifkan" : "Akun belum dihapus"}>
            <p>{failure}</p>
          </Notice>
        ) : null}
        {blocked ? (
          <Notice tone="warning" title="Akun ini masih memiliki dokumen">
            <p>{owns}</p>
            <p>
              {canDeactivate
                ? "Dokumen dan riwayatnya harus tetap tercatat atas nama pembuatnya. Nonaktifkan akun ini; dokumennya tetap tersimpan."
                : "Akun ini sudah nonaktif. Dokumennya tetap tersimpan atas namanya."}
            </p>
          </Notice>
        ) : (
          <p className="confirm-text">
            Akun ini dihapus permanen beserta percakapan Pluto miliknya. Tindakan ini tidak dapat dibatalkan. Akun yang masih memiliki dokumen hanya dapat dinonaktifkan.
          </p>
        )}
      </div>
    </Dialog>
  );
}
